import type { FormatSettings } from '../../../lib/format';
import { businessDate } from '../../../lib/format';

/**
 * A report export as a typed table, read from the API's CSV (every report has one). The Excel file
 * and the printed / PDF document are both built from it, so they always match the CSV.
 */
export type ColumnKind = 'text' | 'number' | 'date';

export interface ReportColumn {
  header: string;
  kind: ColumnKind;
  /** Decimal places shown for a number column (the most any value has). */
  decimals: number;
}

export type ReportCell = string | number | null;

export interface ReportTable {
  /** File name without extension, e.g. `sales-by-day-2026-10-01-to-2026-10-20`. */
  filename: string;
  columns: ReportColumn[];
  rows: ReportCell[][];
  /** Rows that are totals ("Total", "Grand total"): printed in bold. */
  totalRows: Set<number>;
}

/** What the export header shows above the table. */
export interface ReportMeta {
  shopName: string;
  title: string;
  /** e.g. "1 – 20 Oct 2026"; omitted for point-in-time reports without one. */
  period?: string;
  generatedAt: string;
}

/** RFC 4180 parser (quoted fields, doubled quotes, CRLF), dropping a leading byte order mark. */
export function parseCsv(text: string): string[][] {
  const src = text.replace(/^\uFEFF/, '');
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src.charAt(i);
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i++;
        } else quoted = false;
      } else field += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ',') {
      row.push(field);
      field = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else field += ch;
  }
  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

const NUMBER = /^-?\d+(?:\.(\d+))?$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
/** Identifiers stay text even when they look like numbers (leading zeros, no thousands commas). */
const ID_HEADER =
  /\b(sku|code|barcode|phone|mobile|number|no\.?|#|reference|ref|tax no|ntn|strn|account)\b/i;
const MONEY_HEADER =
  /\b(cost|price|value|amount|total|net|tax|balance|debit|credit|paid|due|profit|revenue|sales|purchases|cash|discount)\b/i;
const TOTAL = /^(grand )?totals?\b/i;

/**
 * Money arrives with exactly 2 places and keeps them; quantities arrive with 4 (`12.0000`) and show
 * only the places actually used.
 */
function columnDecimals(header: string, values: string[]): number {
  const raw = values.map((v) => NUMBER.exec(v)?.[1] ?? '');
  if (raw.every((d) => d.length === 2)) return 2;
  const used = Math.min(4, Math.max(0, ...raw.map((d) => d.replace(/0+$/, '').length)));
  // Unit costs and prices come with 4 places; they are still money.
  return MONEY_HEADER.test(header) && raw.some((d) => d.length > 0) ? Math.max(2, used) : used;
}

/** Types each column from its values and converts the cells (CSV → typed table). */
export function toReportTable(csv: string, filename: string): ReportTable {
  const [header = [], ...body] = parseCsv(csv).filter((r) => r.some((c) => c !== ''));
  const totalRows = new Set<number>();
  body.forEach((r, i) => {
    if (TOTAL.test(r[0] ?? '')) totalRows.add(i);
  });
  const columns: ReportColumn[] = header.map((h, c) => {
    const values = body
      .map((r) => r[c] ?? '')
      .filter((v, i) => v !== '' && !(c === 0 && totalRows.has(i)));
    const text: ReportColumn = { header: h, kind: 'text', decimals: 0 };
    if (values.length === 0 || ID_HEADER.test(h)) return text;
    if (values.every((v) => NUMBER.test(v)))
      return {
        header: h,
        kind: 'number',
        decimals: columnDecimals(h, values),
      };
    if (values.every((v) => DATE.test(v))) return { header: h, kind: 'date', decimals: 0 };
    return text;
  });
  const rows = body.map((r) =>
    columns.map((col, c): ReportCell => {
      const v = r[c] ?? '';
      if (v === '') return null;
      if (col.kind === 'number' && NUMBER.test(v)) return Number(v);
      // Undo the API's spreadsheet formula guard (a leading apostrophe) for display.
      return /^'[=+\-@]/.test(v) ? v.slice(1) : v;
    }),
  );
  return { filename, columns, rows, totalRows };
}

/** A cell as printed: grouped numbers with the column's decimals, dates in the shop's style. */
export function formatCell(
  value: ReportCell,
  column: ReportColumn,
  format: FormatSettings,
): string {
  if (value === null) return '';
  if (column.kind === 'number' && typeof value === 'number') {
    const opts = { minimumFractionDigits: column.decimals, maximumFractionDigits: column.decimals };
    try {
      return new Intl.NumberFormat(format.locale, opts).format(value);
    } catch {
      return new Intl.NumberFormat('en', opts).format(value);
    }
  }
  if (column.kind === 'date' && typeof value === 'string') return businessDate(value, format);
  return String(value);
}
